const COUNTER_DURATION = 2.2;
const COUNTER_STAGGER = 0.12;
const COUNTER_EASE = "power3.inOut";

const odometerCache = new WeakMap();

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function buildOdometer(el) {
  if (odometerCache.has(el)) return odometerCache.get(el);

  const end = Number(el.dataset.counterEnd);
  if (!Number.isFinite(end) || end < 0) return null;

  const duration = Number(el.dataset.counterDuration) || COUNTER_DURATION;
  const start = Number(el.dataset.counterStart);
  const from = Number.isFinite(start) && start >= 0 && start <= end ? Math.floor(start) : 0;

  el.textContent = "";
  el.classList.add("counter-odometer");
  el.setAttribute("role", "text");
  el.setAttribute("aria-label", String(end));

  const viewport = document.createElement("span");
  viewport.className = "counter-odometer__viewport";

  const track = document.createElement("span");
  track.className = "counter-odometer__track";

  for (let i = from; i <= end; i += 1) {
    const row = document.createElement("span");
    row.className = "counter-odometer__value";
    row.textContent = String(i);
    track.appendChild(row);
  }

  viewport.appendChild(track);
  el.appendChild(viewport);

  const rows = [...track.children];
  let rowHeight = rows[0]?.offsetHeight || 0;

  if (!rowHeight) {
    const computed = getComputedStyle(el);
    const fontSize = Number.parseFloat(computed.fontSize) || 16;
    const lineHeight = computed.lineHeight;

    rowHeight = lineHeight === "normal" ? fontSize : Number.parseFloat(lineHeight) || fontSize;
  }

  const finalWidth = rows[rows.length - 1]?.offsetWidth || 0;

  if (rowHeight) {
    viewport.style.height = `${rowHeight}px`;
  }

  if (finalWidth) {
    viewport.style.minWidth = `${finalWidth}px`;
  }

  track.style.transform = `translate3d(0, 0, 0)`;

  const data = { el, track, end: end - from, rowHeight, duration };
  odometerCache.set(el, data);
  el.dataset.odometerReady = "true";

  return data;
}

function animateOdometer(data, index = 0) {
  if (!data?.track || !data.rowHeight) return;

  const offset = data.end * data.rowHeight;

  if (prefersReducedMotion()) {
    data.track.style.transform = `translate3d(0, ${-offset}px, 0)`;
    return;
  }

  const gsap = window.gsap;
  if (!gsap) {
    data.track.style.transform = `translate3d(0, ${-offset}px, 0)`;
    return;
  }

  gsap.fromTo(
    data.track,
    { y: 0 },
    {
      y: -offset,
      duration: data.duration,
      delay: index * COUNTER_STAGGER,
      ease: COUNTER_EASE,
      force3D: true,
    },
  );
}

function startCountersIn(root, selector) {
  if (!root || root.dataset.countersStarted === "true") return;

  const counters = root.querySelectorAll(selector);
  if (!counters.length) return;

  counters.forEach((el, index) => {
    const data = buildOdometer(el);
    if (data) animateOdometer(data, index);
  });

  root.dataset.countersStarted = "true";
}

const COUNTER_ROOT_SELECTOR =
  ".services-facts, .about-section-2__facts, .about-facts, .info__facts, .method-cards__stat";
const COUNTER_ITEM_SELECTOR = ".uncode-counter[data-counter-end]";

function startFactCounters(root) {
  startCountersIn(root, COUNTER_ITEM_SELECTOR);
}

function findCounterRoot(el) {
  return el?.closest?.(COUNTER_ROOT_SELECTOR) ?? null;
}

export function initCounters() {
  document.querySelectorAll(COUNTER_ROOT_SELECTOR).forEach((root) => {
    if (root.classList.contains("is-inview")) {
      startFactCounters(root);
    }
  });

  window.addEventListener("startCounters", (event) => {
    const detail = event?.detail || {};
    if (detail.way !== "enter") return;
    const root = findCounterRoot(detail.target);
    if (root) startFactCounters(root);
  });
}
